/**
 * Combat-event → log-line formatting (ADR-0015 §12 middle band).
 *
 * Pure: turns a `CombatEvent` (HEX-52 surface) into a single human line. The
 * overlay diffs `state.log` by length and renders the new tail through this.
 * Kept out of the render code so the phrasing is unit-testable.
 */

import type { CombatEvent, Side } from '../cardEngine';

/** Resolves a side to its display name. The overlay passes the combatant names
 * (`'You'` for the player reads better than the literal player name in-line). */
export type SideNamer = (side: Side) => string;

export function formatEvent(event: CombatEvent, name: SideNamer): string {
  switch (event.kind) {
    case 'turnStart':
      return `${name(event.side)} rolls ${event.faces.join(' ')}`;
    case 'cardPlayed':
      return `${name(event.side)} plays ${event.cardName}`;
    case 'damage': {
      const blockedNote = event.blocked > 0 ? ` (${event.blocked} blocked)` : '';
      return `${name(event.source)} hits ${name(event.target)} for ${event.amount}${blockedNote} → ${event.hpAfter} HP`;
    }
    case 'block':
      return `${name(event.side)} gains ${event.amount} block (${event.total})`;
    case 'statusApplied': {
      const s = event.status;
      return `${name(event.target)} gets ${s.status} ${s.magnitude}×${s.duration}`;
    }
    case 'reroll':
      return `${name(event.side)} re-rolls ${event.count} ${plural(event.count, 'die', 'dice')}`;
    case 'rollFresh':
      return event.fizzled
        ? `${name(event.side)} rolls fresh ${event.faces.join(' ')} — fizzles, no effect`
        : `${name(event.side)} rolls fresh ${event.faces.join(' ')}`;
    case 'trapArmed':
      return `${name(event.side)} arms a trap`;
    case 'trapCharged':
      return `${name(event.side)} charges a trap (${event.charges})`;
    case 'trapFired':
      return `${possessive(name(event.side))} ${event.cardName} FIRES`;
    case 'cardFrozen':
      return `${possessive(name(event.side))} ${event.cardName} is frozen`;
    case 'poisonTick':
      return `${name(event.side)} takes ${event.amount} poison → ${event.hpAfter} HP`;
    case 'heal':
      return `${name(event.side)} heals ${event.amount} → ${event.hpAfter} HP`;
    case 'turnEnd':
      return `${name(event.side)} ends turn`;
    case 'retreatRolled':
      return event.escaped
        ? `You retreat — rolled ${event.face} (odd), and slip away`
        : `Retreat fails — rolled ${event.face} (even); you forfeit your next turn`;
    case 'playerTurnSkipped':
      return 'You are too rattled to act — turn skipped';
    case 'combatEnded':
      return `${name(event.victor)} wins`;
  }
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** Possessive form of a side's display name — `'You'` reads as `'Your'`, any
 * other name takes `'s` (so the trap/freeze lines aren't `"You's Cold Snap"`). */
function possessive(name: string): string {
  return name === 'You' ? 'Your' : `${name}'s`;
}
